import type { QueryHost } from "./query-executor.js";
import { collectMissingProps } from "./task-property-utils.js";
import type { ExplorationTracker, StalenessSignal } from "./exploration-tracker.js";

const PM_PLAN_FILE_TOOLS = new Set(["Write", "Edit", "MultiEdit"]);

const DESTRUCTIVE_PATTERNS: { name: string; re: RegExp }[] = [
  {
    name: "git push --force (without --force-with-lease)",
    re: /git\s+push\s+(?:-f\b|--force(?!-with-lease))/,
  },
  { name: "git push --delete", re: /git\s+push\s+(?:-d\b|--delete\b)/ },
  { name: "git reset --hard", re: /git\s+reset\s+--hard\b/ },
  {
    name: "rm -rf /",
    re: /rm\s+(?:-[a-zA-Z]*r[a-zA-Z]*f|-[a-zA-Z]*f[a-zA-Z]*r|--recursive\s+--force)\s+\/(?!\S)/,
  },
  { name: "sudo rm", re: /\bsudo\s+rm\b/ },
  { name: "chmod world-writable", re: /\bchmod\s+(?:-R\s+)?[0-7]*7{2,3}\b/ },
  { name: "dd to device", re: /\bdd\s+.*\bof=\/dev\// },
  { name: "redirect to block device", re: />\s*\/dev\/(?:sd[a-z]|nvme\d|xvd[a-z])/ },
  { name: "mkfs filesystem creation", re: /\bmkfs(?:\.|\s)/ },
  { name: "shutdown/poweroff/halt/reboot", re: /\b(?:shutdown|poweroff|halt|reboot)\b/ },
  { name: "fork bomb", re: /:\(\)\s*\{\s*:\|\s*:&\s*\}\s*;\s*:/ },
];

function matchesDestructive(cmd: string): string | null {
  for (const { name, re } of DESTRUCTIVE_PATTERNS) {
    if (re.test(cmd)) return name;
  }
  return null;
}

type ToolResult =
  | { behavior: "allow"; updatedInput?: Record<string, unknown> }
  | { behavior: "deny"; message: string };

function isPlanFile(input: Record<string, unknown>): boolean {
  const filePath = String(input.file_path ?? input.path ?? "");
  return filePath.includes(".claude/plans/");
}

function handleDiscoveryToolAccess(toolName: string, input: Record<string, unknown>): ToolResult {
  if (PM_PLAN_FILE_TOOLS.has(toolName)) {
    if (isPlanFile(input)) {
      return { behavior: "allow", updatedInput: input };
    }
    return {
      behavior: "deny",
      message: "Discovery mode is read-only. File writes are restricted to plan files.",
    };
  }
  return { behavior: "allow", updatedInput: input };
}

function handleBuildingToolAccess(toolName: string, input: Record<string, unknown>): ToolResult {
  if (toolName === "Bash") {
    const cmd = String(input.command ?? "");
    const matched = matchesDestructive(cmd);
    if (matched) {
      return {
        behavior: "deny",
        message: `Destructive operation blocked (${matched}). Use safer alternatives.`,
      };
    }
  }
  return { behavior: "allow", updatedInput: input };
}

function handleReviewToolAccess(toolName: string, input: Record<string, unknown>): ToolResult {
  // Review mode has full write access — reviewer can make direct fixes
  return handleBuildingToolAccess(toolName, input);
}

function handleAutoToolAccess(
  toolName: string,
  input: Record<string, unknown>,
  hasExitedPlanMode: boolean,
  isParentTask: boolean,
): ToolResult {
  if (hasExitedPlanMode) {
    return isParentTask
      ? handleReviewToolAccess(toolName, input)
      : handleBuildingToolAccess(toolName, input);
  }
  // Pre-ExitPlanMode: trust the SDK's native plan mode for restrictions.
  return { behavior: "allow", updatedInput: input };
}

function enforceMissingProps(
  host: QueryHost,
  input: Record<string, unknown>,
  missingProps: string[],
): ToolResult | null {
  if (missingProps.length === 0) return null;
  if (input.bypassValidation !== true) {
    return {
      behavior: "deny" as const,
      message: [
        "Cannot exit plan mode. Required task properties are missing:",
        ...missingProps.map((p) => `- ${p}`),
        "",
        "Fill these in using MCP tools (e.g. update_task_plan, update_task_properties), then call ExitPlanMode again.",
        "",
        "If you have a deliberate reason to proceed without them, you must explicitly bypass validation by calling ExitPlanMode with `bypassValidation: true` as a tool argument. Do not bypass unless the team has asked you to — it will be surfaced in chat.",
      ].join("\n"),
    };
  }
  host.connection.postChatMessage(
    `⚠️ [BYPASS] ExitPlanMode forced through with \`bypassValidation: true\` despite missing required properties: ${missingProps.join(", ")}. Please backfill these.`,
  );
  return null;
}

async function handleExitPlanMode(
  host: QueryHost,
  input: Record<string, unknown>,
): Promise<ToolResult> {
  if (host.hasExitedPlanMode) {
    return { behavior: "allow" as const, updatedInput: input };
  }

  try {
    // Flush any locally-written plan files to the server before validation.
    // Awaited so a failed sync surfaces in chat before getTaskProperties is
    // consulted — otherwise we'd validate against stale server-side plan state.
    await host.syncPlanFile();

    const taskProps = await host.connection.getTaskProperties();
    const missingProps = collectMissingProps(taskProps);

    // Validate subtasks have plans if this is a parent task
    if (host.isParentTask) {
      try {
        const subtasks = await host.connection.call("listSubtasks", {
          sessionId: host.connection.sessionId,
        });
        const subtasksWithoutPlans = subtasks.filter(
          (s: { plan?: string | null; title: string }) => !s.plan?.trim(),
        );
        if (subtasksWithoutPlans.length > 0) {
          const names = subtasksWithoutPlans.map((s: { title: string }) => s.title).join(", ");
          missingProps.push(
            `subtask plans — these subtasks are missing plans: ${names} (use update_subtask with plan field)`,
          );
        }
      } catch {
        // If we can't list subtasks, skip this validation
      }
    }

    const gate = enforceMissingProps(host, input, missingProps);
    if (gate) return gate;

    if (host.agentMode === "discovery") {
      host.hasExitedPlanMode = true;
      host.discoveryCompleted = true;
      // Await both the identification trigger and the confirmation chat post
      // before requesting stop. Previously these were fire-and-forget, so a
      // connection drop between the ExitPlanMode call and the stop could lose
      // either the identification event or the confirmation message, leaving
      // the agent with no visible signal that planning actually completed.
      try {
        await host.connection.triggerIdentification();
      } catch (triggerErr) {
        host.connection.postChatMessage(
          `Identification trigger encountered an issue (${triggerErr instanceof Error ? triggerErr.message : "unknown error"}). Icon and agent assignment may use fallbacks.`,
        );
      }
      await host.connection.postChatMessageAwait(
        "Planning complete — awaiting team approval. Icon and agent assignment will be set automatically.",
      );
      // requestStop aborts the SDK event loop only after the above acks land,
      // guaranteeing the team sees a confirmation message for this session.
      // Under the PTY harness this verdict travels back to the CLI over the
      // hook socket — defer the stop a tick so the allow response is written
      // before teardown closes the socket and kills the CLI. (The CLI dies at
      // its plan-approval dialog; the session resumes cleanly on Build.)
      if (host.harnessKind === "pty") {
        setTimeout(() => host.requestStop(), 250);
      } else {
        host.requestStop();
      }
      return { behavior: "allow" as const, updatedInput: input };
    }

    // Auto mode: trigger identification but don't let it block ExitPlanMode.
    // Identification runs asynchronously server-side with fallbacks,
    // and the agent should proceed to building mode regardless.
    try {
      await host.connection.triggerIdentification();
    } catch (triggerErr) {
      host.connection.postChatMessage(
        `Identification trigger encountered an issue (${triggerErr instanceof Error ? triggerErr.message : "unknown error"}). Proceeding to build phase — identification will use fallbacks.`,
      );
    }

    host.hasExitedPlanMode = true;

    // Same-session continuation on BOTH harnesses: the SDK upgrades plan→build
    // permissions natively; under PTY the session presses Enter on the residual
    // plan dialog (planDialogAutoAccept) and continues in acceptEdits with the
    // settings allow-list covering the rest. The mode_changed event below is
    // deliberately unhandled server-side — DB task.agentMode must stay "auto"
    // so computeRunnerMode/canBypassPlanning resolve correctly on pod restarts.
    const newMode = host.isParentTask ? "review" : "building";
    host.connection.sendEvent({ type: "mode_transition", from: "auto", to: newMode });
    host.connection.emitModeChanged(newMode);

    return { behavior: "allow" as const, updatedInput: input };
  } catch (err) {
    return {
      behavior: "deny" as const,
      message: `Identification failed: ${err instanceof Error ? err.message : String(err)}. Fix the issue and try again.`,
    };
  }
}

async function handleAskUserQuestion(
  host: QueryHost,
  input: Record<string, unknown>,
): Promise<ToolResult> {
  const QUESTION_TIMEOUT_MS = 5 * 60 * 1000;
  const questions = input.questions as {
    question: string;
    header: string;
    options: { label: string; description: string; preview?: string }[];
    multiSelect?: boolean;
  }[];

  host.connection.emitStatus("waiting_for_input");
  host.connection.sendEvent({
    type: "tool_use",
    tool: "AskUserQuestion",
    input: JSON.stringify(input),
  });

  const answerPromise = host.connection.askUserQuestion(questions);
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => resolve(null), QUESTION_TIMEOUT_MS);
  });

  const answers = await Promise.race([answerPromise, timeoutPromise]);
  host.connection.emitStatus("running");

  if (!answers || Object.keys(answers).length === 0) {
    return {
      behavior: "deny",
      message:
        "User did not respond to clarifying questions in time. Proceed with your best judgment.",
    };
  }

  return { behavior: "allow", updatedInput: { questions: input.questions, answers } };
}

const EXPLORATION_TOOLS = new Set(["Read", "Grep", "Glob"]);

function trackExploration(
  tracker: ExplorationTracker,
  toolName: string,
  input: Record<string, unknown>,
): StalenessSignal | null {
  if (!EXPLORATION_TOOLS.has(toolName)) return null;

  if (toolName === "Read") {
    const filePath = String(input.file_path ?? "");
    if (filePath) return tracker.recordFileRead(filePath);
  }

  if (toolName === "Grep" || toolName === "Glob") {
    const pattern = String(input.pattern ?? "");
    if (pattern) return tracker.recordSearch(pattern);
  }

  return null;
}

const DENIAL_WARNING_THRESHOLD = 3;
const DENIAL_FORCE_STOP_THRESHOLD = 8;

function handleDenialEscalation(host: QueryHost, consecutiveDenials: number): void {
  if (consecutiveDenials === DENIAL_WARNING_THRESHOLD) {
    host.connection.postChatMessage(
      `⚠️ Multiple tool denials detected. You are in ${host.agentMode} mode — ` +
        `file writes outside .claude/plans/ are not permitted. ` +
        `Focus on creating a plan instead of implementing code changes.`,
    );
  }
  if (consecutiveDenials >= DENIAL_FORCE_STOP_THRESHOLD) {
    host.connection.postChatMessage(
      `Agent force-stopped after ${DENIAL_FORCE_STOP_THRESHOLD} consecutive tool denials. ` +
        `The agent appears stuck — send a message to resume.`,
    );
    host.requestStop();
  }
}

function resolveToolAccess(
  host: QueryHost,
  toolName: string,
  input: Record<string, unknown>,
): ToolResult {
  switch (host.agentMode) {
    case "discovery":
      return handleDiscoveryToolAccess(toolName, input);
    case "building":
      return handleBuildingToolAccess(toolName, input);
    case "review":
      return handleReviewToolAccess(toolName, input);
    case "auto":
      return handleAutoToolAccess(toolName, input, host.hasExitedPlanMode, host.isParentTask);
    default:
      return { behavior: "allow", updatedInput: input };
  }
}

export function buildCanUseTool(
  host: QueryHost,
): (toolName: string, input: Record<string, unknown>) => Promise<ToolResult> {
  let consecutiveDenials = 0;

  return async (toolName, input) => {
    if (
      toolName === "ExitPlanMode" &&
      (host.agentMode === "auto" || host.agentMode === "discovery") &&
      !host.hasExitedPlanMode
    ) {
      return await handleExitPlanMode(host, input);
    }

    // Repeated ExitPlanMode calls after plan mode was already exited in discovery
    // mode should be denied to prevent burning budget without triggering force-stop.
    if (toolName === "ExitPlanMode" && host.agentMode === "discovery" && host.hasExitedPlanMode) {
      return {
        behavior: "deny" as const,
        message:
          "Plan mode has already been exited. The team will transition you to Building mode when ready.",
      };
    }

    if (toolName === "AskUserQuestion") {
      return await handleAskUserQuestion(host, input);
    }

    const result = resolveToolAccess(host, toolName, input);

    // Track exploration patterns during planning to detect analysis paralysis.
    // Only active when tracker exists (discovery/auto-planning) and plan mode hasn't been exited.
    if (result.behavior === "allow" && host.explorationTracker && !host.hasExitedPlanMode) {
      const signal = trackExploration(host.explorationTracker, toolName, input);
      if (signal) {
        host.connection.postChatMessage(signal.message);
      }
    }

    if (result.behavior === "deny") {
      consecutiveDenials++;
      handleDenialEscalation(host, consecutiveDenials);
    } else {
      consecutiveDenials = 0;
    }

    return result;
  };
}
